import React, { useState, useEffect, useContext, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Chip,
  Tooltip,
  LinearProgress
} from "@mui/material";
import RestoreIcon from "@mui/icons-material/Restore";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import DownloadIcon from "@mui/icons-material/Download";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import { fetchJson } from "../../../utils/fetchJson";
import { UserContext } from "../../../contexts/UserContext";

export default function BackupsPage() {
  const { t } = useTranslation();
  const user = useContext(UserContext);
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");
  const [snackMsg, setSnackMsg] = useState("");
  const [snackOpen, setSnackOpen] = useState(false);

  // Restore dialog
  const [restoreDialog, setRestoreDialog] = useState(false);
  const [restoreName, setRestoreName] = useState("");
  const [restorePreview, setRestorePreview] = useState(null);
  const [restoring, setRestoring] = useState(false);

  // Delete dialog
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleteName, setDeleteName] = useState("");

  const canManage = useMemo(() => {
    if (user?.role === 'SystemAdmin') return true;
    const perms = user && Array.isArray(user.systemPermissions) ? user.systemPermissions : null;
    if (!perms) return true;
    if (perms.includes('*')) return true;
    return perms.includes('security');
  }, [user]);

  useEffect(() => { loadBackups(); }, []);

  const loadBackups = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchJson("/api/system-backups/list");
      setBackups(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || "Failed to load backups");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!canManage) return;
    setCreating(true);
    setError("");
    try {
      const data = await fetchJson("/api/system-backups/create", { method: "POST" });
      setSnackMsg(t("backupsPage.created", "System backup created successfully") + ` (${data.backup?.sizeMB || '?'} MB)`);
      setSnackOpen(true);
      loadBackups();
    } catch (err) {
      setError(err.message || "Create failed");
    } finally {
      setCreating(false);
    }
  };

  const handleDownload = (name) => {
    const token = localStorage.getItem("accessToken") || sessionStorage.getItem("accessToken");
    const configuredBaseRaw = (
      process.env.REACT_APP_API_BASE_URL ||
      process.env.VITE_API_BASE_URL ||
      process.env.NEXT_PUBLIC_API_BASE_URL ||
      ''
    ).trim();
    const browserHost = typeof window !== 'undefined' ? window.location.hostname : '';
    const isLocalBrowserHost = ['localhost', '127.0.0.1', '0.0.0.0'].includes(browserHost);
    const isLocalNodeEnv = process.env.NODE_ENV === 'development';
    const baseRaw = configuredBaseRaw || ((isLocalBrowserHost || isLocalNodeEnv) ? 'http://localhost:5000' : '');
    const base = (baseRaw || '').replace(/\/$/, '');
    window.open(`${base}/api/system-backups/download/${encodeURIComponent(name)}?token=${token}`, "_blank");
  };

  // Restore: step 1 – preview
  const openRestoreDialog = async (name) => {
    if (!canManage) return;
    setRestoreName(name);
    setRestorePreview(null);
    setRestoreDialog(true);
    setError("");
    try {
      const data = await fetchJson("/api/system-backups/restore", {
        method: "POST",
        body: { backupName: name }
      });
      setRestorePreview(data.preview);
    } catch (err) {
      setError(err.message);
    }
  };

  // Restore: step 2 – execute
  const confirmRestore = async () => {
    if (!canManage) return;
    setRestoring(true);
    setError("");
    try {
      await fetchJson("/api/system-backups/restore", {
        method: "POST",
        body: { backupName: restoreName, confirmRestore: true }
      });
      setSnackMsg(t("backupsPage.restored", "System restored successfully"));
      setSnackOpen(true);
      setRestoreDialog(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setRestoring(false);
    }
  };

  // Delete
  const openDeleteDialog = (name) => { setDeleteName(name); setDeleteDialog(true); };
  const confirmDelete = async () => {
    if (!canManage) return;
    try {
      await fetchJson(`/api/system-backups/delete/${encodeURIComponent(deleteName)}`, { method: "DELETE" });
      setSnackMsg("Backup deleted");
      setSnackOpen(true);
      setDeleteDialog(false);
      loadBackups();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Typography variant="h4" gutterBottom>
        {t("backupsPage.title", "System Backups & Recovery")}
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        {t("backupsPage.desc", "Create, download, and restore full system-wide backups covering all schools and system data.")}
      </Typography>

      <Button variant="contained" startIcon={<AddIcon />} sx={{ mb: 2 }} onClick={handleCreate} disabled={creating || !canManage}>
        {creating ? t("backupsPage.creating", "Creating...") : t("backupsPage.create", "Create System Backup")}
      </Button>

      {creating && <LinearProgress sx={{ mb: 2 }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>{error}</Alert>}

      {loading ? (
        <Box display="flex" justifyContent="center" mt={4}><CircularProgress /></Box>
      ) : (
        <Paper variant="outlined" sx={{ overflow: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>{t("backupsPage.name", "Backup Name")}</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{t("backupsPage.date", "Date")}</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">{t("backupsPage.size", "Size")}</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">{t("backupsPage.actionsCol", "Actions")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {backups.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">{t("backupsPage.none", "No backups available")}</TableCell>
                </TableRow>
              ) : backups.map((b) => (
                <TableRow key={b.name} hover>
                  <TableCell>{b.name}</TableCell>
                  <TableCell>{new Date(b.createdAt).toLocaleString()}</TableCell>
                  <TableCell align="right"><Chip label={`${b.sizeMB} MB`} size="small" /></TableCell>
                  <TableCell align="center">
                    <Tooltip title={t("backupsPage.download", "Download")}>
                      <IconButton size="small" onClick={() => handleDownload(b.name)}><DownloadIcon fontSize="small" /></IconButton>
                    </Tooltip>
                    <Tooltip title={t("backupsPage.restore", "Restore")}>
                      <IconButton size="small" color="warning" onClick={() => openRestoreDialog(b.name)} disabled={!canManage}><RestoreIcon fontSize="small" /></IconButton>
                    </Tooltip>
                    <Tooltip title={t("backupsPage.deleteBtn", "Delete")}>
                      <IconButton size="small" color="error" onClick={() => openDeleteDialog(b.name)} disabled={!canManage}><DeleteIcon fontSize="small" /></IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* Restore Dialog */}
      <Dialog open={restoreDialog} onClose={() => !restoring && setRestoreDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t("backupsPage.restoreTitle", "Restore System Backup")}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t("backupsPage.restoreWarn", "This will overwrite ALL existing data in the system. This action cannot be undone.")}
          </Alert>
          <Typography variant="subtitle2" gutterBottom>{restoreName}</Typography>
          {restorePreview ? (
            <Box sx={{ maxHeight: 300, overflow: "auto", mt: 1 }}>
              <Typography variant="body2" color="text.secondary" mb={1}>
                {t("backupsPage.previewLabel", "Records to restore:")}
              </Typography>
              <Table size="small">
                <TableBody>
                  {Object.entries(restorePreview.recordCounts || {}).filter(([,c]) => c > 0).map(([table, count]) => (
                    <TableRow key={table}>
                      <TableCell>{table}</TableCell>
                      <TableCell align="right"><Chip label={count} size="small" color="primary" /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          ) : (
            <Box display="flex" justifyContent="center" py={2}><CircularProgress size={24} /></Box>
          )}
          {restoring && <LinearProgress sx={{ mt: 2 }} />}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRestoreDialog(false)} disabled={restoring}>
            {t("backupsPage.cancel", "Cancel")}
          </Button>
          <Button variant="contained" color="error" onClick={confirmRestore} disabled={restoring || !restorePreview || !canManage}>
            {restoring ? t("backupsPage.restoring", "Restoring...") : t("backupsPage.confirmRestore", "Confirm Restore")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t("backupsPage.deleteTitle", "Delete Backup")}</DialogTitle>
        <DialogContent>
          <Typography>{t("backupsPage.deleteConfirm", "Are you sure you want to permanently delete this backup?")}</Typography>
          <Typography variant="subtitle2" mt={1}>{deleteName}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>{t("backupsPage.cancel", "Cancel")}</Button>
          <Button variant="contained" color="error" onClick={confirmDelete} disabled={!canManage}>{t("backupsPage.deleteBtn", "Delete")}</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackOpen}
        autoHideDuration={4000}
        onClose={() => setSnackOpen(false)}
        message={snackMsg}
        action={
          <IconButton size="small" color="inherit" onClick={() => setSnackOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        }
      />
    </Box>
  );
}
